import mammoth from "mammoth";
import Tesseract from "tesseract.js";
import * as pdfjs from "pdfjs-dist/build/pdf";
import pdfWorker from "pdfjs-dist/build/pdf.worker.js";

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorker;

export const extractTextFromWord = async (file: File): Promise<string> => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    return (
      result.value.trim() || "No meaningful text found in this Word document."
    );
  } catch (error) {
    console.error("Error extracting text from Word document:", error);
    throw new Error("Failed to process Word document.");
  }
};

export const extractTextFromPDF = async (
  file: File
): Promise<string> => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjs.getDocument({
      data: new Uint8Array(arrayBuffer),
    }).promise;
    if (!Promise.allSettled) {
      Promise.allSettled = function (promises) {
        return Promise.all(
          promises.map((p) =>
            Promise.resolve(p)
              .then((value) => ({ status: "fulfilled", value }))
              .catch((reason) => ({ status: "rejected", reason }))
          )
        );
      };
    }
    let finalText = "";
    const totalPages = pdf.numPages;

    for (let i = 1; i <= totalPages; i++) {
      const page = await pdf.getPage(i);

      // Try normal text extraction
      const content = await page.getTextContent();
      const pageText = content.items
        .map((item: any) => item.str || "")
        .join(" ")
        .trim();

      // Detect if page contains images
      const ops = await page.getOperatorList();
      const hasImage = ops.fnArray.some(
        fn =>
          fn === pdfjs.OPS.paintImageXObject ||
          fn === pdfjs.OPS.paintInlineImageXObject
      );

      let textToAppend = "";

      // Decision logic
      if (!pageText || !pageText?.length || hasImage) {
        // OCR only this page
        const ocrText = await extractTextWithTesseract(page);
        textToAppend = ocrText;
      } else {
        textToAppend = pageText;
      }

      finalText += textToAppend + "\n";

      // Progress update
    }

    return finalText.trim() || "No meaningful text found.";
  } catch (error) {
    // console.error("Error extracting text from PDF:", error);
    return "";
  }
};

export const extractTextWithTesseract = async (
  page: any,
  onProgress?: (percent: number) => void
): Promise<string> => {
  let worker: any;

  try {
    worker = await Tesseract.createWorker();
    await worker.loadLanguage("eng");
    await worker.initialize("eng");
    const viewport = page.getViewport({ scale: 1.2 });

    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");

    if (!context) {
      throw new Error("Failed to create 2D context for canvas");
    }

    canvas.width = viewport.width;
    canvas.height = viewport.height;
    await page.render({ canvasContext: context, viewport }).promise;
    onProgress?.(30);

    const imageDataURL = canvas.toDataURL("image/png");

    // OCR
    const { data } = await worker.recognize(imageDataURL);
    onProgress?.(90);

    return data.text.trim() || "";
  } catch (error) {
    console.error("Error extracting text with Tesseract:", error);
    return "";
  } finally {
    if (worker) {
      await worker.terminate();
    }
  }
};

export const generateCopilotSummary = (responseText) => {
  try {
    // Clean and isolate JSON sections dynamically
    const cleanedText = responseText
      .replace(/```json|```/g, "") // Remove JSON markers
      .trim();

    // Extract summary
    const summaryMatch = cleanedText.match(/^1\.\s*([\s\S]*?)(?=2\.)/);
    const summary = summaryMatch
      ? summaryMatch[1].trim()
      : "No summary generated.";

    // Extract keys data
    // const keysMatch = cleanedText.match(/2\.\s*(\[[\s\S]*?\])/);
    const keysMatch = cleanedText.match(/2\.\s*.*?\s*(\[[\s\S]*?\])/);
    let Keyvalue = [];
    if (keysMatch) {
      try {
        Keyvalue = JSON.parse(keysMatch[1]).filter(
          (item) => item.value && item.value !== "N/A"
        );
      } catch (error) {
        console.error("Error parsing Keyvalue JSON:", error);
      }
    }
    const KeyvalueParagraph = Keyvalue.map(({ key, value }) => {
      switch (key.toLowerCase()) {
        case "title":
          return `The title of this contract is ${value}.`;
        case "party a":
          return `Party A involved in this contract is ${value}.`;
        case "party b":
          return `Party B involved in this contract is ${value}.`;
        case "amount":
          return `The total contract amount is ${value}.`;
        case "contract start date":
          return `The contract starts on ${value}.`;
        case "contract end date":
          return `The contract ends on ${value}.`;
        case "contract duration":
          return `The contract duration is ${value}.`;
        default:
          return `The "${key}" is ${value}.`;
      }
    }).join(" ");

    // Extract obligations
    // const obligationsMatch = cleanedText.match(/3\.\s*(\[[\s\S]*?\])/);
    const obligationsMatch = cleanedText.match(/3\.\s*.*?\s*(\[[\s\S]*?\])/);
    let Obligation = [];
    if (obligationsMatch) {
      try {
        Obligation = JSON.parse(obligationsMatch[1]);
      } catch (error) {
        console.error("Error parsing Obligation JSON:", error);
      }
    }

    const tagsMatch = cleanedText.match(/4\.\s*(\{[\s\S]*?\})/);
    let TagsCount = { risk: 0, tax: 0, law: 0 };
    if (tagsMatch) {
      try {
        TagsCount = JSON.parse(tagsMatch[1]);
      } catch (error) {
        console.error("Error parsing TagsCount JSON:", error);
      }
    }

    return { Summary: summary, KeyvalueParagraph, Obligation, TagsCount };
  } catch (parseError) {
    console.error("Failed to parse response as JSON:", parseError);
    throw new Error(
      "Response format is invalid. Failed to parse the response."
    );
  }
};